import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { usePerson } from "@/shared/hooks/useAppContext";
import { MODULE_DEFINITIONS, type ModuleStatus } from "@/shared/constants/moduleDefinitions";
import { ESTATE_MODULES } from "@/shared/constants/estateModules";

interface TaskRow {
  task_id: string;
  status: string;
  started_at: string | null;
}

interface ModuleProgressItem {
  id: string;
  status: ModuleStatus;
  progress: number;
}

interface UseModuleProgressReturn {
  modules: ModuleProgressItem[];
  completedCount: number;
  activeModuleId: string | null;
  allCompleted: boolean;
  isLoading: boolean;
  taskStatusMap: Map<string, string>;
  taskStartedAtMap: Map<string, string | null>;
}

export function useModuleProgress(): UseModuleProgressReturn {
  const person = usePerson();
  const estateId = person?.estate_id;

  const { data: taskRows = [], isLoading } = useQuery({
    queryKey: ["moduleProgress", estateId],
    queryFn: async () => {
      if (!estateId) return [];

      const { data, error } = await supabase
        .from("estate_tasks")
        .select("task_id, status, started_at")
        .eq("estate_id", estateId);

      if (error) throw error;
      return (data ?? []) as TaskRow[];
    },
    enabled: !!estateId,
    staleTime: 0,
  });

  const derived = useMemo(() => {
    const taskStatusMap = new Map(taskRows.map((r) => [r.task_id, r.status]));
    const taskStartedAtMap = new Map(taskRows.map((r) => [r.task_id, r.started_at]));

    let activeModuleId: string | null = null;
    let completedCount = 0;

    const modules: ModuleProgressItem[] = MODULE_DEFINITIONS.map((def) => {
      const estateModule = ESTATE_MODULES.find((m) => m.id === def.id);
      const tasks = estateModule?.tasks ?? [];

      if (tasks.length === 0) {
        if (!activeModuleId) activeModuleId = def.id;
        return { id: def.id, status: "not_started" as ModuleStatus, progress: 0 };
      }

      const completedTasks = tasks.filter(
        (t) => taskStatusMap.get(t.taskId) === "completed",
      ).length;
      const progress = completedTasks / tasks.length;

      let status: ModuleStatus;
      if (completedTasks === tasks.length) {
        status = "completed";
        completedCount++;
      } else if (
        completedTasks > 0 ||
        tasks.some((t) => taskStatusMap.get(t.taskId) === "in_progress")
      ) {
        status = "in_progress";
      } else {
        status = "not_started";
      }

      if (!activeModuleId && status !== "completed") {
        activeModuleId = def.id;
      }

      return { id: def.id, status, progress };
    });

    return {
      modules,
      completedCount,
      activeModuleId,
      allCompleted: completedCount === MODULE_DEFINITIONS.length,
      taskStatusMap,
      taskStartedAtMap,
    };
  }, [taskRows]);

  return { ...derived, isLoading };
}
